import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { withAppHOC } from '../../../hoc';
import PageHeading from '../../../components/PageHeader/PageHeader';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import NoData from '../../../components/NoData/NoData';
import { Image } from '../../../components/Image';
import { TableBuilder } from '../../../components/TableElements';
import {
  StyledIconTitleContainer,
  StyledIconContainer,
  StyledTitleContainer,
  StyledSpan,
  StyledFiltersContainer,
  StyledLink,
} from '../../../components/Styled';
import {
  MessageBox,
  Form,
  Fieldset,
  Button,
  ResetButton,
  Label,
  Dropdown,
  Switch,
  CustomConfirmButton,
} from '../../../components/FormElements';
import FiltersButton from '../../../components/FiltersButton/FiltersButton';
import Pagination from '../../../components/Pagination/Pagination';
import API from '../../../api';
import { API_RESPONSE_TYPES, PERMISSION_TYPES } from '../../../constants';
import {
  getSearchParams,
  shouldRedirect,
  redirectToPageOne,
  formatCurrency,
} from '../../../utils';
import StatusHighliter from '../../../components/StatusHighliter/StatusHighliter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';

const SellerMarginsList = (props) => {
  const [searchParams] = useSearchParams();
  const pUsersId = searchParams.get('usersId');
  const pApisId = searchParams.get('apisId') || '';
  const [paginationData, setPaginationData] = useState({
    pageNumber: getSearchParams('pageNumber') || 1,
    recordsPerPage: getSearchParams('recordsPerPage') || 10,
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [isFiltering, setIsFiltering] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [responseStatus, setResponseStatus] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const [circlesId, setCirclesId] = useState('');
  const [categoriesId, setCategoriesId] = useState('');
  const [margins, setMargins] = useState([]);
  const [users, setUsers] = useState([]);
  const [operators, setOperators] = useState([]);
  const [apis, setApis] = useState([]);
  const [fUsersId, setFusersId] = useState(pUsersId ?? '');
  const [fOperatorsId, setFoperatorsId] = useState('');
  const [fcirclesId, setFcirclesId] = useState('');
  const [fCategoriesId, setFCategoriesId] = useState('');
  const [fApisId, setFapisId] = useState(pApisId ?? '');
  const [circles, setCircles] = useState([]);
  const [categories, setCategories] = useState([]);

  const resetForm = () => {
    setFusersId('');
    setFoperatorsId('');
    setFapisId('');
    setFcirclesId('');
    setFCategoriesId('');
    readData();
  };

  const readUsers = useCallback(() => {
    setIsLoading(true);
    setResponseStatus('');
    setResponseMessage('');
    API.get('/accounts-management/users?pageNumber=1&recordsPerPage=100000')
      .then((response) => {
        const { status, message, data } = response.data;
        if (status === API_RESPONSE_TYPES.FAILED) {
          setResponseStatus(status);
          setResponseMessage(message);
        } else {
          setUsers(data);
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILED);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const readOperators = useCallback(() => {
    setIsLoading(true);
    setResponseStatus('');
    setResponseMessage('');
    API.get(
      '/recharge-configurations/operators?pageNumber=1&recordsPerPage=100000'
    )
      .then((response) => {
        const { status, message, data } = response.data;
        if (status === API_RESPONSE_TYPES.FAILED) {
          setResponseStatus(status);
          setResponseMessage(message);
        } else {
          setOperators(data);
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILED);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const readCircles = useCallback(() => {
    API.get("/recharge-configurations/circles").then((response) => {
      const { status, data } = response.data;
      if (status === API_RESPONSE_TYPES.SUCCESS) {
        setCircles(data);
      }
    });
  }, []);

  const readCategories = useCallback(() => {
    setIsLoading(true);
    setResponseStatus("");
    setResponseMessage("");
    API.get(
      `/recharge-configurations/categories/?pageNumber=1&recordsPerPage=100000`
    )
      .then((response) => {
        const { status, message, data } = response.data;
        if (status === API_RESPONSE_TYPES.FAILED) {
          setResponseStatus(status);
          setResponseMessage(message);
        } else {
          setCategories(data);
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILED);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const readApis = useCallback(() => {
    setIsLoading(true);
    setResponseStatus('');
    setResponseMessage('');
    API.get(
      '/recharge-configurations/seller-apis?pageNumber=1&recordsPerPage=10000000'
    )
      .then((response) => {
        const { status, message, data } = response.data;
        if (status === API_RESPONSE_TYPES.FAILED) {
          setResponseStatus(status);
          setResponseMessage(message);
        } else {
          setApis(data);
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILED);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const readData = useCallback(
    (fUsersId = '', fOperatorsId = '', fApisId = '', fcirclesId = '', fCategoriesId = '') => {
      setIsLoading(true);
      setResponseStatus('');
      setResponseMessage('');
      setMargins([]);
      API.get(
        `/recharge-configurations/seller-margins/?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}&fUsersId=${fUsersId}&fOperatorsId=${fOperatorsId}&fApisId=${fApisId}&fCirclesId=${fcirclesId}&fCategoriesId=${fCategoriesId}`
      )
        .then((response) => {
          const { status, message, data, pageInfo } = response.data;
          if (shouldRedirect(data?.length, paginationData?.pageNumber)) {
            redirectToPageOne();
          } else {
            setMargins(data);
            if (status === API_RESPONSE_TYPES.FAILED) {
              setResponseStatus(status);
              setResponseMessage(message);
            } else {
              setTotalRecords(pageInfo.totalRecords);
            }
          }
        })
        .catch((error) => {
          setResponseStatus(API_RESPONSE_TYPES.FAILED);
          setResponseMessage(error.message);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [paginationData]
  );

  const reloadData = () => {
    readData(fUsersId, fOperatorsId, fApisId, fcirclesId, fCategoriesId);
  };

  const blockMargin = (usersId, operatorsId) => {
    setIsLoading(true);
    setResponseStatus('');
    setResponseMessage('');
    const formData = {
      usersId,
      operatorsId,
      profileType: 'Seller',
      remark: 'Blocked by Admin',
    };
    API.post('/recharge-configurations/operator-blocker', formData)
      .then((response) => {
        const { status, message } = response.data;
        if (status === API_RESPONSE_TYPES.FAILED) {
          setResponseStatus(status);
          setResponseMessage(message);
        } else {
          reloadData();
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILED);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const unblockMargin = (id) => {
    setIsLoading(true);
    setResponseStatus('');
    setResponseMessage('');
    API.delete(`/recharge-configurations/operator-blocker/${id}`)
      .then((response) => {
        const { status, message } = response.data;
        if (status === API_RESPONSE_TYPES.FAILED) {
          setResponseStatus(status);
          setResponseMessage(message);
        } else {
          reloadData();
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILED);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const updateData = (value, data, key) => {
    const newData = { ...data };
    newData[key] = value === true ? `Active` : `Inactive`;
    setIsLoading(true);
    setResponseStatus('');
    setResponseMessage('');
    API.put(
      `/recharge-configurations/seller-margins/${newData.id}?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}`,
      newData
    )
      .then((response) => {
        const { status, message, data } = response.data;
        if (shouldRedirect(data?.length, paginationData?.pageNumber)) {
          redirectToPageOne();
        } else {
          if (status === API_RESPONSE_TYPES.FAILED) {
            setResponseStatus(status);
            setResponseMessage(message);
          } else {
            setMargins(data);
          }
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILED);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const deleteData = (id) => {
    setIsLoading(true);
    setResponseStatus('');
    setResponseMessage('');
    API.delete(
      `/recharge-configurations/seller-margins/${id}?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}&fOperatorsId=${fOperatorsId}`
    )
      .then((response) => {
        const { status, message, data } = response.data;
        if (shouldRedirect(data?.length, paginationData?.pageNumber)) {
          redirectToPageOne();
        } else {
          if (status === API_RESPONSE_TYPES.FAILED) {
            setResponseStatus(status);
            setResponseMessage(message);
          } else {
            setMargins(data);
          }
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILED);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const filterData = (e) => {
    e.preventDefault();
    setIsFiltering(true);
  };

  useEffect(() => {
    if (isFiltering) {
      setPaginationData((paginationData) => ({
        pageNumber: 1,
        recordsPerPage: paginationData.recordsPerPage,
      }));
      setIsFiltering((isFiltering) => !isFiltering);
    }
  }, [isFiltering]);

  useEffect(() => {
    if (!isFiltering) {
      readData(fUsersId, fOperatorsId, fApisId, fcirclesId, fCategoriesId);
    }
  }, [paginationData, readData]);

  useEffect(() => {
    readUsers();
    readOperators();
    readApis();
    readCircles();
    readCategories();
  }, [readUsers, readOperators, readApis, readCircles, readCategories]);

  const usersOptions = [{ title: '-- SELECT USER --', value: '' }];
  users.forEach(({ id, fullName }) =>
    usersOptions.push({
      title: fullName,
      value: id?.toString(),
    })
  );

  const operatorsOptions = [{ title: '-- SELECT OPERATOR --', value: '' }];
  operators.forEach(({ id, operatorName }) =>
    operatorsOptions.push({
      title: operatorName,
      value: id?.toString(),
    })
  );

  const apisOptions = [{ title: '-- SELECT API --', value: '' }];
  apis.forEach(({ id, apiName, fullName }) =>
    apisOptions.push({
      title: apiName,
      value: id?.toString(),
      subTitle: fullName,
    })
  );

  const circleOptions = [{ title: "-- SELECT CIRCLE --", value: "" }];
  if (circles && circles.length > 0) {
    circles.forEach(({ id, circleName }) =>
      circleOptions.push({
        title: circleName,
        value: id,
      })
    );
  }

  const categoriesOptions = [{ title: "-- SELECT CATEGORY --", value: "" }];
  if (categories && categories.length > 0) {
    categories.forEach(({ id, category }) =>
      categoriesOptions.push({
        title: category,
        value: id,
      })
    );
  }

  const [showFilters, setShowFilters] = useState(false);
  const onClickShowFilters = () => {
    setShowFilters((showFilters) => !showFilters);
  };

  const { accountsPermissions, subRoutes } = props;
  let updateLink = '';
  let updateTitle = '';
  const canUpdate =
    accountsPermissions.includes(PERMISSION_TYPES.UPDATE) || false;
  const canDelete =
    accountsPermissions.includes(PERMISSION_TYPES.DELETE) || false;
  if (canUpdate) {
    const { title, path } = subRoutes.find(
      ({ permissionRequired }) => permissionRequired === PERMISSION_TYPES.UPDATE
    );
    updateLink = path;
    updateTitle = title;
  }

  return (
    <>
      <PageHeading {...props} reloadData={reloadData} />
      <FiltersButton onClick={onClickShowFilters} value={showFilters} />
      <StyledFiltersContainer className={showFilters ? 'show' : ''}>
        <Form method="POST" action="#" onSubmit={filterData}>
          <Fieldset>
            <Label>User</Label>
            <Dropdown
              value={fUsersId}
              onChange={setFusersId}
              placeholder="User"
              disabled={isLoading}
              options={usersOptions}
              searchable
            />
          </Fieldset>
          <Fieldset>
            <Label>Operator</Label>
            <Dropdown
              value={fOperatorsId}
              onChange={setFoperatorsId}
              placeholder="Operator"
              disabled={isLoading}
              options={operatorsOptions}
            />
          </Fieldset>

          <Fieldset>
            <Label>Circle</Label>
            <Dropdown
              placeholder="Circle"
              value={fcirclesId}
              onChange={setFcirclesId}
              options={circleOptions}
              disabled={isLoading}
              searchable
            />
          </Fieldset>
          <Fieldset>
            <Label>Category</Label>
            <Dropdown
              placeholder="Category"
              value={fCategoriesId}
              onChange={setFCategoriesId}
              options={categoriesOptions}
              disabled={isLoading}
              searchable
            />
          </Fieldset>
          <Fieldset>
            <Label>Seller API</Label>
            <Dropdown
              value={fApisId}
              onChange={setFapisId}
              placeholder="Seller API"
              disabled={isLoading}
              options={apisOptions}
            />
          </Fieldset>
          <Fieldset>
            <Button disabled={isLoading} type="submit">
              Filter
            </Button>
            <ResetButton disabled={isLoading} type="button" onClick={resetForm}>
              Clear
            </ResetButton>
          </Fieldset>
        </Form>
      </StyledFiltersContainer>
      <LoadingSpinner
        isLoading={responseStatus === '' && margins?.length === 0 && isLoading}
      />
      <NoData
        status={
          responseStatus !== API_RESPONSE_TYPES.FAILED &&
          !isLoading &&
          margins?.length === 0
        }
        message={`No margins found`}
      />
      {responseStatus === API_RESPONSE_TYPES.FAILED && (
        <MessageBox status={responseStatus} message={responseMessage} />
      )}
      {margins?.length > 0 && (
        <>
          <TableBuilder
            isLoading={margins?.length !== 0 && isLoading}
            tableHeadings={[
              {
                title: '',
                dataSelector: 'id',
                sticky: true,
                canSort: false,
                width: '100px',
                CellRenderer: (value) => (
                  <>
                    {canUpdate && (
                      <StyledLink
                        to={updateLink.replace(':id', value)}
                        title={updateTitle}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </StyledLink>
                    )}
                    {canDelete && (
                      <CustomConfirmButton onClick={() => deleteData(value)} />
                    )}
                  </>
                ),
              },
              {
                title: 'User',
                dataSelector: 'fullName',
                dataType: 'string',
                CellRenderer: (value, item) => (
                  <>
                    {value}
                    <StyledSpan>{item.mobileNumber}</StyledSpan>
                  </>
                ),
              },
              {
                title: 'Operator',
                dataSelector: 'operatorName',
                dataType: 'string',
                CellRenderer: (value, item) => (
                  <StyledIconTitleContainer>
                    <StyledIconContainer>
                      <Image
                        source={
                          item.operatorLogo
                            ? item.operatorLogo
                            : '/no-image.png'
                        }
                        alt={value}
                        style={{ width: '50px', height: '50px' }}
                      />
                    </StyledIconContainer>
                    <StyledTitleContainer>{value}</StyledTitleContainer>
                  </StyledIconTitleContainer>
                ),
              },
              {
                title: 'API',
                dataSelector: 'apiName',
                dataType: 'string',
              },
              {
                title: 'Circle',
                dataSelector: 'circleName',
                dataType: 'string',
              },
              {
                title: 'Category',
                dataSelector: 'category',
                dataType: 'string',
              },
              // {
              //   title: 'Margin Type',
              //   dataSelector: 'marginType',
              //   dataType: 'string',
              // },
              // {
              //   title: 'GST Type',
              //   dataSelector: 'gstType',
              //   dataType: 'string',
              //   CellRenderer: (value) => (value ? value : 'N/A'),
              // },
              {
                title: 'Operator Code',
                dataSelector: 'operatorCode',
                dataType: 'string',
              },
              {
                title: 'Amount',
                dataSelector: 'amount',
                dataType: 'string',
              },
              {
                title: 'Margin',
                dataSelector: 'margin',
                dataType: 'number',
                CellRenderer: (value) => `${value}%`,
              },
              // {
              //   title: 'Roffer Margin',
              //   dataSelector: 'rofferMargin',
              //   dataType: 'number',
              //   CellRenderer: (value) => `${value}%`,
              // },
              {
                title: 'Max Limit',
                dataSelector: 'maxLimit',
                dataType: 'number',
                CellRenderer: (value) => formatCurrency(value),
              },
              {
                title: 'Used Limit',
                dataSelector: 'usedLimit',
                dataType: 'number',
                CellRenderer: (value) => formatCurrency(value),
              },
              {
                title: 'Margin Status',
                dataSelector: 'marginStatus',
                dataType: 'string',
                align: 'center',
                canSort: false,
                CellRenderer: (value, data) => {
                  return (
                    <Switch
                      value={value === 'Active'}
                      onChange={(value) => updateData(value, data, 'marginStatus')}
                    />
                  );
                },
              },
              {
                title: 'Admin Status',
                dataSelector: 'blockStatus',
                dataType: 'string',
                align: 'center',
                canSort: false,
                CellRenderer: (value, data) => {
                  return (
                    <Switch
                      value={value === 'Active'}
                      onChange={() =>
                        value === 'Active'
                          ? blockMargin(data.usersId, data.operatorsId)
                          : unblockMargin(data.blockId)
                      }
                    />
                  );
                },
              },
            ]}
            tableData={margins}
          />
          <Pagination
            totalRecords={totalRecords}
            paginationData={paginationData}
            setPaginationData={setPaginationData}
          />
        </>
      )}
    </>
  );
};

export default withAppHOC(SellerMarginsList);
